import { useCallback } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client'
import set from 'lodash/fp/set'
import update from 'lodash/fp/update'
import compose from 'lodash/fp/compose'
import { useArtistInfo } from 'modules/Projects/serviceHooks'

import { IUpdateProjectArguments, IArtist } from 'shared/interfaces'
import { useLoading } from 'shared/hooks'
import { GET_PROJECT } from 'modulesCommon/graphql/queries'
import {
  UPDATE_PROJECT,
  UPDATE_ARTIST,
  DELETE_PROJECT,
  ARCHIVE_PROJECT,
  ACTIVATE_PROJECT,
  FIND_USER_BY_EMAIL,
  FIND_USER_BY_ID
} from '../constants'

import { objToSnakeCase } from '../helpers'
import { IProjectForm, IUpdateArtistArgument, IUser, IOwner, IProject } from '../types'
import { growl } from '@wmgtech/legato'
import { useNavigate } from 'react-router'
import { useCreateArtistMutation } from '../../../modulesCommon/graphql/mutations/createArtist.types'

export const useProject = (id?: string) => {
  const { activeArtist } = useArtistInfo(id)
  const navigate = useNavigate()
  const [getUserQuery] = useLazyQuery<{ findUserByEmail: IUser }, { email: string }>(FIND_USER_BY_EMAIL)
  const [getUserByIdQuery] = useLazyQuery<{ getUser: IUser }, { id: number }>(FIND_USER_BY_ID)

  const [updateProjectMutation, { loading: updateProjectLoading }] = useMutation<
    { updateCampaign: IProject },
    { input: IUpdateProjectArguments },
    { email: string }
  >(UPDATE_PROJECT, {
    update(cache, { data }, { context }) {
      const res = cache.readQuery<{ getCampaign: IProject }>({
        query: GET_PROJECT,
        variables: {
          id
        }
      })
      if (res) {
        cache.writeQuery<{ getCampaign: IProject }>({
          query: GET_PROJECT,
          variables: {
            id
          },
          data: compose(
            set(['getCampaign', 'user', 'email'])(context?.email),
            set(['getCampaign', 'user', 'id'])((data?.updateCampaign.owner_id ?? '').toString()),
            update(['getCampaign'])((d) => ({ ...d, ...data?.updateCampaign }))
          )(res)
        })
      }
    }
  })

  const [createArtist, { loading: createArtistLoading }] = useCreateArtistMutation()

  const [updateArtistMutation, { loading: updateArtistLoading }] = useMutation<
    { updateArtist: IArtist },
    { input: IUpdateArtistArgument }
  >(UPDATE_ARTIST)

  const [deleteProjectMutation, { loading: deleteProjectLoading }] = useMutation<
    { removeCampaign: IProject },
    { id: string }
  >(DELETE_PROJECT, {
    onCompleted: () => {
      growl.success({
        message: 'Project deleted successfully'
      })
      navigate('/')
    },
    update(cache) {
      cache.evict({ id: `Campaign:${id}` })
    },
    onError: ({ graphQLErrors }) => {
      if (graphQLErrors.length > 0) {
        growl.error({
          message: graphQLErrors[0].message
        })
      }
    }
  })

  const [archiveProjectMutation, { loading: archiveProjectLoading }] = useMutation<
    { archiveCampaign: IProject },
    { id: string | undefined }
  >(ARCHIVE_PROJECT, {
    update(cache) {
      const res = cache.readQuery<{ getCampaign: IProject }>({
        query: GET_PROJECT,
        variables: {
          id
        }
      })
      if (res) {
        cache.writeQuery<{ getCampaign: IProject }>({
          query: GET_PROJECT,
          variables: {
            id
          },
          data: set(['getCampaign', 'active'])(false)(res)
        })
      }
    }
  })

  const [activateProjectMutation, { loading: activateProjectLoading }] = useMutation<
    { activateCampaign: IProject },
    { id: string | undefined }
  >(ACTIVATE_PROJECT, {
    update(cache) {
      const res = cache.readQuery<{ getCampaign: IProject }>({
        query: GET_PROJECT,
        variables: {
          id
        }
      })
      if (res) {
        cache.writeQuery<{ getCampaign: IProject }>({
          query: GET_PROJECT,
          variables: {
            id
          },
          data: set(['getCampaign', 'active'])(true)(res)
        })
      }
    }
  })

  const getUser = useCallback<(email: string) => Promise<IOwner>>(
    async (email) => {
      const { data } = await getUserQuery({
        variables: {
          email
        }
      })
      if (data?.findUserByEmail.abilities.canCreateCampaign) {
        return {
          id: parseInt(data.findUserByEmail.id),
          email: data.findUserByEmail.email
        }
      }
      throw new Error('Invalid user email')
    },
  [getUserQuery]
  )

  const getUserById = useCallback<(id: number) => Promise<IOwner>>(
    async (id) => {
      const { data } = await getUserByIdQuery({
        variables: {
          id
        }
      })
      if (data?.getUser.abilities.canCreateCampaign) {
        return {
          id: parseInt(data.getUser.id),
          email: data.getUser.email
        }
      }
      throw new Error('Invalid user email')
    },
  [getUserByIdQuery]
  )

  const onUpdateProject  = useCallback<(data: IProjectForm, ownerEmail: string, ownerId: number) => Promise<void>>(
    async (data, ownerEmail, ownerId: number) => {
      await updateProjectMutation({
        context: {
          email: ownerEmail
        },
        variables: {
          input: objToSnakeCase<IUpdateProjectArguments>({ ...data, owner_id: ownerId })
        }
      })
    }, [updateProjectMutation])

  const updateProject = useCallback<(data: IProjectForm) => Promise<void>>(
    async ({ artistLabelId, artistName, opusId, gpsStatus, ownerEmail, ...data }) => {
      if (!ownerEmail) {
        return
      }
      const { id } = await getUser(ownerEmail)

      // opus_id = id if artist is in gcdm only, so we dont have it in our DB and should create a new one
      if (`${data.artistId}` !== `${activeArtist.id}` && `${data.artistId}` === `${opusId}`) {
        const { data: createdArtist } = await createArtist({
          variables: {
            input: {
              name: artistName ?? '',
              label_id: artistLabelId ?? '',
              gps_status: gpsStatus,
              opus_id: opusId
            }
          }
        })

        if (createdArtist?.createArtist) {
          return await onUpdateProject({
            ...data,
            artistId: Number(createdArtist?.createArtist?.id),
          }, ownerEmail, id)
        }
      }

      await Promise.all([
        onUpdateProject(data, ownerEmail, id),
        updateArtistMutation({
          variables: {
            input: {
              id: `${data.artistId ?? activeArtist.id}`,
              label_id: activeArtist.label_id,
              gps_status: gpsStatus
            }
          }
        })
      ])
    },
  [activeArtist.id, activeArtist.label_id, getUser, updateArtistMutation, onUpdateProject, createArtist]
  )

  const deleteProject = useCallback<(id: string) => Promise<void>>(
    async (id) => {
      await deleteProjectMutation({
        variables: {
          id
        }
      })
    },
  [deleteProjectMutation]
  )

  const archiveProject = useCallback<(id: string | undefined) => Promise<void>>(
    async (id) => {
      await archiveProjectMutation({
        variables: {
          id
        }
      })
    },
  [archiveProjectMutation]
  )

  const activateProject = useCallback<(id: string | undefined) => Promise<void>>(
    async (id) => {
      await activateProjectMutation({
        variables: {
          id
        }
      })
    },
  [activateProjectMutation]
  )

  useLoading({
    isLoading:
      updateProjectLoading ||
      updateArtistLoading ||
      deleteProjectLoading ||
      archiveProjectLoading ||
      activateProjectLoading ||
      createArtistLoading
  })

  return {
    updateProject,
    deleteProject,
    archiveProject,
    activateProject,
    getUser,
    getUserById
  }
}
